import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LabeledRadio } from '@/components/LabeledRadio/LabeledRadio';
import { DcrFormController } from '@/features/DataChangeRequest/DcrFormController/DcrFormController';
import { FilteredField } from '@/features/DataChangeRequest/FilteredField/FilteredField';
import { HcoSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcoFieldNames';
import { HcpSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcpFieldNames';

import { normalizeMultiPrimaryDefaults } from '../_hooks/normalizeMultiPrimaryDefaults';
import type { Option } from '../_types/Dropdown.types';
import { FormTextField } from '../FormTextField/FormTextField';

import { FormAutocompleteField } from './FormAutocompleteField';
import { multiPrimaryAutocompleteFieldStyles as sx } from './MultiPrimaryAutocompleteField.styles';

export const EMPTY_PRIMARY_ROW = {
	id: '',
	name: '',
	isPrimary: true,
	fieldName: undefined
};

const EMPTY_NON_PRIMARY_ROW = {
	id: '',
	name: '',
	isPrimary: false,
	fieldName: undefined
};

export type MultiPrimaryItem = {
	id: string;
	name?: string;
	isPrimary?: boolean;
	fieldName?: string;
};

type Props = {
	name: string;
	section: HcoSection | HcpSection;
	label: string;
	max?: number;
	disabled?: boolean;
	indicateError?: boolean;
	disableNonInitialItems?: boolean;
	canAddItems?: boolean;
	defaultItems?:
		Array<MultiPrimaryItem> | MultiPrimaryItem | string | null | undefined;
	id?: string;
	hideLabel?: boolean;
	readOnly?: boolean;
	hidePrimaryButton?: boolean;
	filterable?: boolean;
	oneRequired?: boolean;
	isRequired?: boolean;
	canRemoveItems?: boolean;
} & (
	| { isAutocomplete: true; options: Option[] }
	| { isAutocomplete?: false; options?: never }
);

export const MultiPrimaryAutocompleteField = ({
	name,
	section,
	label,
	max,
	disabled,
	indicateError,
	defaultItems,
	id,
	disableNonInitialItems,
	canAddItems = true,
	hideLabel = false,
	readOnly = false,
	hidePrimaryButton = false,
	filterable = true,
	oneRequired = false,
	isRequired = false,
	canRemoveItems = false,
	...rest
}: Props) => {
	const { t } = useTranslation();
	const isAutocomplete = 'isAutocomplete' in rest && rest.isAutocomplete;
	const options = isAutocomplete ? rest.options : [];

	const { control, setValue, reset, getValues, formState } = useFormContext();
	const { fields, append, remove, replace } = useFieldArray({
		control,
		name
	});

	const [rowSnapshots, setRowSnapshots] = useState<Record<
		string,
		MultiPrimaryItem
	> | null>(null);

	const initialLoadDataRef = useRef<MultiPrimaryItem[] | null>(null);
	const fieldDefaults = formState.defaultValues?.[name];
	const prevFieldDefaultsRef = useRef(fieldDefaults);
	const hasInitializedRef = useRef(false);

	const values = useWatch({ control, name }) as MultiPrimaryItem[] | undefined;

	const allSelectedIds = new Set(
		(values || []).map(item => item.id).filter(val => !!val)
	);

	useEffect(() => {
		if (!hasInitializedRef.current) {
			hasInitializedRef.current = true;
			prevFieldDefaultsRef.current = fieldDefaults;
			return;
		}
		if (prevFieldDefaultsRef.current !== fieldDefaults) {
			prevFieldDefaultsRef.current = fieldDefaults;
			setRowSnapshots(null);
			initialLoadDataRef.current = null;
		}
	}, [fieldDefaults]);

	useEffect(() => {
		// If we are in "Creation Mode" (empty fields), we must inject one empty primary row so the user has an input to type in
		if (!fields || fields.length === 0) {
			const normalized = normalizeMultiPrimaryDefaults(defaultItems);

			const items =
				normalized.length > 0
					? normalized.map((item, i) => ({
							...item,
							isPrimary: i === 0 || !!item.isPrimary
						}))
					: [EMPTY_PRIMARY_ROW];

			initialLoadDataRef.current = JSON.parse(JSON.stringify(items));

			// Note (Ben): This is required to override the defaultValues for this field, otherwise changes elsewhere in the form can mark this as dirty, when it shouldn't
			reset(
				{
					...getValues(),
					[name]: items
				},
				{
					keepDefaultValues: false,
					keepDirty: true
				}
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (rowSnapshots) return;

		if (
			initialLoadDataRef.current &&
			fields.length === initialLoadDataRef.current.length
		) {
			const snapshot: Record<string, MultiPrimaryItem> = {};
			fields.forEach((field, index) => {
				const originalItem = initialLoadDataRef.current![index];
				if (originalItem) {
					snapshot[field.id] = originalItem;
				}
			});
			setRowSnapshots(snapshot);
			initialLoadDataRef.current = null;
		} else if (fields.length > 0 && values && values.length === fields.length) {
			const snapshot: Record<string, MultiPrimaryItem> = {};
			const currentValuesCopy = JSON.parse(JSON.stringify(values));

			fields.forEach((field, index) => {
				snapshot[field.id] = currentValuesCopy[index];
			});
			setRowSnapshots(snapshot);
		}
	}, [fields, values, rowSnapshots]);

	const makePrimary = (targetIndex: number) => {
		fields.forEach((_, i) => {
			setValue(`${name}.${i}.isPrimary`, i === targetIndex, {
				shouldDirty: true
			});
		});
	};

	const handleRemove = (index: number) => {
		if (fields.length === 1) {
			replace([EMPTY_PRIMARY_ROW]);
			return;
		}
		const wasPrimary = values?.[index]?.isPrimary;
		remove(index);
		if (wasPrimary && fields.length > 0) {
			setValue(`${name}.0.isPrimary`, true, { shouldDirty: true });
		}
	};

	const handleAdd = () => {
		if (max && fields.length >= max) return;
		append(EMPTY_NON_PRIMARY_ROW);
	};

	const canAddMore = !max || fields.length < max;

	return (
		<FilteredField name={name} section={section} enabled={filterable}>
			<Box sx={sx.root}>
				{fields.map((field: MultiPrimaryItem, index) => {
					return (
						<DcrFormController
							key={field.id}
							name={`${name}.${index}.id`}
							control={control}
							filterable={false}
							render={({
								field: { value, onChange },
								fieldState: { error }
							}) => (
								<DcrFormController
									name={`${name}.${index}.isPrimary`}
									control={control}
									filterable={false}
									render={({ field: primaryField }) => {
										let isRowDirty = false;

										if (rowSnapshots) {
											const originalState = rowSnapshots[field.id];

											if (originalState) {
												const idChanged =
													(value ?? '') !== (originalState.id ?? '');
												const primaryChanged =
													(primaryField.value ?? false) !==
													(originalState.isPrimary ?? false);

												isRowDirty = idChanged || primaryChanged;
											} else {
												isRowDirty = !!value;
											}
										}

										const isReadOnly =
											readOnly || (disableNonInitialItems ? index > 0 : false);

										const filteredOptions = options.filter(
											opt => !allSelectedIds.has(opt.id) || opt.id === value
										);

										return (
											<Box sx={sx.fieldContainer}>
												{isAutocomplete ? (
													<FormAutocompleteField
														label={index === 0 ? label : ''}
														hideLabel={hideLabel}
														placeholder={label}
														name={field.fieldName || name}
														section={section}
														options={filteredOptions}
														defaultValue={value ?? ''}
														handleOnDropdownItemSelect={item => {
															const newId = item ? item.id : '';
															onChange(newId);

															const newName = item ? item.name : '';
															setValue(`${name}.${index}.name`, newName, {
																shouldDirty: true
															});
														}}
														isDirty={isRowDirty}
														isReadOnly={isReadOnly}
														indicateError={indicateError || !!error}
														isRequired={oneRequired ? index === 0 : isRequired}
														canBeRemoved={
															canRemoveItems && (fields.length > 1 || !!value)
														}
														onRemove={() => handleRemove(index)}
														canAddNewItem={
															canAddItems &&
															canAddMore &&
															index === fields.length - 1
														}
														onAddNewItemClick={handleAdd}
														id={id}
														primaryRadio={
															!hidePrimaryButton && (
																<LabeledRadio
																	label={t('Primary')}
																	disabled={isReadOnly}
																	checked={primaryField.value ?? false}
																	onChange={() => makePrimary(index)}
																	indicateError={indicateError}
																	isDirty={isRowDirty}
																/>
															)
														}
													/>
												) : (
													<FormTextField
														label={index === 0 ? label : ''}
														placeholder={label}
														name={`${name}.${index}.id`}
														defaultValue={value ?? ''}
														onChange={onChange}
														isDirty={isRowDirty}
														indicateError={indicateError || !!error}
														disabled={disabled}
														canBeRemoved={fields.length > 1 || !!value}
														onRemove={() => handleRemove(index)}
														canAddNewItem={
															canAddItems &&
															canAddMore &&
															index === fields.length - 1
														}
														onAddNewItemClick={handleAdd}
														id={id}
													/>
												)}
											</Box>
										);
									}}
								/>
							)}
						/>
					);
				})}
			</Box>
		</FilteredField>
	);
};
